import "katex/dist/katex.min.css";

import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Latex from "react-latex-next";

import { AnswerSimple, BookmarkButton, Lightbox } from "components";
import { AssignmentImage } from "components/AssignmentImage";
import IconArrowBackBig from "components/Icons/IconArrowBackBig/IconArrowBackBig";
import Video from "components/Video/Video";

import type { AssignmentDetailFragment } from "api/generated";

import { useAssetUrlResolver } from "utils/hooks/useAssetUrlResolver";

interface Props {
  assignment: AssignmentDetailFragment;
  answerType: "success" | "warning" | "danger";
  isShown: boolean;
  questionIndex: number;
  toggleModal: () => void;
  isGoBackShown?: boolean;
  children: ReactNode;
  userAssignmentId: string;
}

const SolutionModal: FC<Props> = ({
  assignment,
  questionIndex,
  answerType,
  children,
  isShown,
  toggleModal,
  isGoBackShown,
  userAssignmentId,
}) => {
  const { t } = useTranslation(["common"]);
  const [isAssignmentVisible, setAssignmentVisible] = useState(false);

  const analysis =
    assignment.analysis?.hasSharedAnalysis === true
      ? assignment.analysis.sharedAnalysis
      : assignment.assignment?.questions[questionIndex].solutionAnalysis;

  const imageAnalysisUrl = useAssetUrlResolver(analysis?.image);
  const imageAssignmentUrl = useAssetUrlResolver(assignment?.assignment?.assignmentImage);
  const thumbHash = assignment?.assignment?.assignmentImageThumbHash;
  const imageDimensions = assignment?.assignment?.AssignmentImageDimensions;

  const getTitle = () => {
    const heading = assignment.assignment?.questions[questionIndex]?.heading;
    if (typeof heading === "string" && heading.length > 0) return heading;

    return "";
  };

  const toggleAssignment = () => {
    setAssignmentVisible(val => !val);
  };

  return (
    // Rozbor nemá mít titulek (rozhodl zadavatel 31. 8.) — v hlavičce je jen tlačítko
    // zpět vlevo a křížek vpravo, dialog se pojmenuje neviditelně. [TD-344]
    <Lightbox
      show={isShown}
      onHide={toggleModal}
      className={"modal--exercise"}
      size={"lg"}
      ariaLabel={t("btn.analysisDesktop", { ns: "exercise" })}
      headerStart={
        <Button size={"sm"} variant={"navigation"} onClick={toggleModal}>
          <IconArrowBackBig />
        </Button>
      }
      footer={
        <div className="modal-footer-btn-wrapper">
          <div className="modal-footer-btn-stretch">
            <BookmarkButton userAssignmentId={userAssignmentId} />
            <Button variant={"outline-secondary-darker"} onClick={toggleModal} className={"flex-grow-0"}>
              {t("close")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="exercise">
        <div className="exercise__body">
          <div>
            <Button variant="link" onClick={toggleAssignment} className="p-0 mb-20px">
              {isAssignmentVisible ? "Skrýt zadání" : "Zobrazit zadání"}
            </Button>

            {isAssignmentVisible ? (
              <div>
                <Latex>{assignment?.assignment?.assignmentText ?? ""}</Latex>
                {!!assignment?.assignment?.assignmentImage && (
                  <AssignmentImage
                    src={imageAssignmentUrl}
                    thumbHash={thumbHash}
                    width={imageDimensions?.width}
                    height={imageDimensions?.height}
                  />
                )}
              </div>
            ) : null}
          </div>

          <Latex>{getTitle()}</Latex>
          <AnswerSimple type={answerType}>{children}</AnswerSimple>
          <hr className={"in-modal thin gray-200"} />
          <p>
            {/* TODO: Translate */}
            <strong>Postup řešení</strong>
          </p>
          {typeof analysis?.explainerVideo === "string" && analysis.explainerVideo.length > 0 ? (
            <>
              <div className={"mt-3"}>
                <Video videoId={analysis.explainerVideo} />
              </div>
            </>
          ) : null}
          {typeof analysis?.writtenVideo === "string" && analysis.writtenVideo.length > 0 ? (
            <>
              <div className={"video-wrapper mt-3"}>
                <Video videoId={analysis.writtenVideo} />
              </div>
            </>
          ) : null}
          {imageAnalysisUrl !== null ? (
            <div className="mt-3">
              <img src={imageAnalysisUrl} />
            </div>
          ) : null}
          <div className="mt-3">
            <Latex>{analysis?.text ?? ""}</Latex>
          </div>

          {/* Temporary: transcript (EN + CZ) below the video analysis. Content is rich text (HTML) from the CMS. */}
          {assignment.assignment?.transcriptEn?.trim() ? (
            <div className="mt-4">
              <h6 className="fw-bold">Transcript (EN)</h6>
              <div dangerouslySetInnerHTML={{ __html: assignment.assignment.transcriptEn }} />
            </div>
          ) : null}
          {assignment.assignment?.transcriptCs?.trim() ? (
            <div className="mt-4">
              <h6 className="fw-bold">Přepis (CZ)</h6>
              <div dangerouslySetInnerHTML={{ __html: assignment.assignment.transcriptCs }} />
            </div>
          ) : null}
        </div>
      </div>
    </Lightbox>
  );
};

export default SolutionModal;
